import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Chip,
    Stack
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { updateGardenGroup, deleteGardenGroup } from "../../api/groups";
import { useTranslate } from "../Translate/TranslateContext";

export default function GroupModal({ open, onClose, group, onRefresh }) {
    const { translate } = useTranslate();
    const [groupName, setGroupName] = useState("");
    const [loading, setLoading] = useState(false);

    // Sync state whenever the selected group changes
    useEffect(() => {
        if (group) {
            setGroupName(group.group_name || "");
        }
    }, [group]);

    if (!group) return null;

    const handleSave = async () => {
        if (!groupName.trim()) return;

        try {
            setLoading(true);
            // Map existing gardens back into the payload format expected by your update API
            const gardenIds = group.gardens?.map((g) => g.garden_id) || [];
            
            await updateGardenGroup(group.group_id, {
                group_name: groupName.trim(),
                garden_ids: gardenIds,
            });
            
            await onRefresh();
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            await deleteGardenGroup(group.group_id);
            await onRefresh();
            onClose();
        } catch (error) {
            console.error(error);
        } {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {translate("manage_group")}
                </Typography>
                <IconButton color="error" onClick={handleDelete} disabled={loading}>
                    <DeleteIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        label={translate("group_name")}
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        disabled={loading}
                    />

                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {translate("assigned_gardens")}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {group.gardens?.length ? (
                                group.gardens.map((garden) => (
                                    <Chip 
                                        key={garden.garden_id} 
                                        label={garden.garden_name} 
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {translate("no_gardens_in_group")}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {translate("cancel")}
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={loading || !groupName.trim()}
                >
                    {translate("save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}